import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowUpRight, Linkedin } from "lucide-react";
import { CONTACT } from "../data/scan";
import { useNewsletterSubscribe } from "../lib/useNewsletterSubscribe";

// Pages where the full Operational Brief signup section appears immediately
// before the footer — hide any duplicate signup control in the footer.
const HAS_FULL_SIGNUP_ABOVE = (pathname) =>
  pathname === "/" || pathname === "/insights" || pathname.startsWith("/insights/");

const FooterSignupForm = () => {
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState("");
  const { state, error, submit, reset } = useNewsletterSubscribe();
  const isSending = state === "sending";
  const isSuccess = state === "success";

  const onChange = (setter) => (e) => {
    if (state === "error") reset();
    setter(e.target.value);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    await submit({ email, website });
  };

  if (isSuccess) {
    return (
      <div
        data-testid="footer-signup-success"
        role="status"
        aria-live="polite"
        className="mt-8 max-w-sm border-l-2 border-terra pl-4"
      >
        <p className="text-[12px] font-semibold uppercase tracking-[0.2em] text-terra">
          Check your inbox
        </p>
        <p className="mt-2 text-[13px] leading-relaxed text-paper/70">
          We have sent an email to confirm your subscription to The Operational Brief. Your
          subscription will become active after you select the confirmation link.
        </p>
      </div>
    );
  }

  return (
    <form
      data-testid="footer-signup-form"
      className="relative mt-8 max-w-sm"
      onSubmit={onSubmit}
      noValidate
    >
      <label
        htmlFor="footer-signup-email"
        className="block text-[12px] font-semibold uppercase tracking-[0.2em] text-paper/40"
      >
        Subscribe to The Operational Brief
      </label>
      <div className="mt-3 flex gap-2">
        <input
          id="footer-signup-email"
          data-testid="footer-signup-email"
          type="email"
          placeholder="Work email"
          value={email}
          onChange={onChange(setEmail)}
          disabled={isSending}
          aria-invalid={state === "error"}
          aria-describedby={state === "error" ? "footer-signup-error" : undefined}
          aria-label="Work email"
          className="flex-1 min-w-0 bg-transparent border-b border-paper/25 outline-none py-2 text-sm text-paper placeholder:text-paper/40 disabled:opacity-60 disabled:cursor-not-allowed focus:border-paper/60 transition-colors"
        />
        <button
          type="submit"
          data-testid="footer-signup-submit"
          disabled={isSending}
          className="shrink-0 inline-flex items-center bg-paper/10 text-paper text-[11px] font-semibold uppercase tracking-[0.14em] px-4 py-2 border border-paper/20 hover:bg-terra hover:border-terra transition-colors duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {isSending ? "Sending..." : "Subscribe"}
        </button>
      </div>
      <div aria-hidden="true" className="absolute -left-[9999px] w-px h-px overflow-hidden">
        <label>
          Website
          <input
            type="text"
            tabIndex={-1}
            autoComplete="off"
            data-testid="footer-signup-website"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
          />
        </label>
      </div>
      <p className="mt-4 text-[11px] leading-relaxed text-paper/50">
        By subscribing, you’ll receive The Operational Brief and occasional relevant updates. You
        can unsubscribe at any time.{" "}
        <Link
          to="/privacy-policy"
          data-testid="footer-signup-privacy-link"
          className="link-rule underline underline-offset-2"
        >
          Privacy Policy
        </Link>
      </p>
      {state === "error" && (
        <p
          id="footer-signup-error"
          data-testid="footer-signup-error"
          role="alert"
          className="mt-3 text-[12px] font-medium text-terra"
        >
          {error}
        </p>
      )}
    </form>
  );
};

export const Footer = () => {
  const { pathname } = useLocation();
  const showCompactSignup = !HAS_FULL_SIGNUP_ABOVE(pathname);

  return (
    <footer data-testid="site-footer" className="bg-ink text-paper">
      <div className="max-w-6xl mx-auto px-6 md:px-12 py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <img
              src="/assets/logo-reverse.png"
              alt="BOS — Behaviour Operating System"
              width="1921"
              height="684"
              loading="lazy"
              decoding="async"
              className="h-14 w-auto"
            />
            <p className="mt-5 max-w-sm text-sm leading-relaxed text-paper/60">
              Behaviour Operating System helps leaders create the clarity required for more consistent execution.
            </p>
            {showCompactSignup && <FooterSignupForm />}
          </div>
          <div className="md:col-span-3">
            <p className="text-[12px] font-semibold uppercase tracking-[0.2em] text-paper/40">Explore</p>
            <ul className="mt-4 space-y-2.5 text-sm">
              {[
                ["/operational-clarity", "Operational Clarity"],
                ["/how-bos-works", "How BOS Works"],
                ["/friction-review", "Operational Friction Review"],
                ["/operational-clarity-review", "Operational Clarity Review"],
                ["/insights", "Insights"],
                ["/executive-guide", "Executive Guide"],
                ["/about", "About"],
              ].map(([to, label]) => (
                <li key={to}>
                  <Link
                    to={to}
                    data-testid={`footer-link-${label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="link-rule text-paper/70 hover:text-paper transition-colors duration-300"
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div className="md:col-span-4">
            <p className="text-[12px] font-semibold uppercase tracking-[0.2em] text-paper/40">Contact</p>
            <ul className="mt-4 space-y-2.5 text-sm text-paper/70">
              <li>{CONTACT.name}</li>
              <li>{CONTACT.location}</li>
              <li>
                <Link
                  to="/contact"
                  data-testid="footer-contact-link"
                  className="link-rule inline-flex items-center gap-1 text-paper/70 hover:text-paper transition-colors duration-300"
                >
                  Contact BOS <ArrowUpRight size={13} />
                </Link>
              </li>
              <li>
                <a
                  data-testid="footer-linkedin-link"
                  href={CONTACT.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Connect with Tristan Todd on LinkedIn"
                  className="inline-flex items-center gap-2.5 text-paper/70 hover:text-paper transition-colors duration-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-terra"
                >
                  <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-paper/45">Connect on</span>
                  <Linkedin size={18} aria-hidden="true" />
                </a>
              </li>
            </ul>
          </div>
        </div>
        <div className="mt-14 pt-6 border-t border-paper/10 flex flex-col md:flex-row md:items-center justify-between gap-3">
          <p className="text-xs text-paper/40">
            © {new Date().getFullYear()} BOS — Behaviour Operating System. Sydney, Australia.
          </p>
          <div className="flex items-center gap-5">
            <Link
              to="/privacy-policy"
              data-testid="footer-link-privacy"
              className="link-rule text-xs text-paper/40 hover:text-paper/70 transition-colors duration-300"
            >
              Privacy Policy
            </Link>
            <Link
              to="/terms-of-use"
              data-testid="footer-link-terms"
              className="link-rule text-xs text-paper/40 hover:text-paper/70 transition-colors duration-300"
            >
              Terms of Use
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
